import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (type) => {
  if (!type) return <InsertDriveFileIcon />;

  if (type.includes('pdf')) {
    return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
  }
  if (type.includes('spreadsheet') || type.includes('excel') || type.includes('csv')) {
    return <TableChartIcon sx={{ color: '#2e7d32' }} />;
  }
  if (type.includes('image')) {
    return <ImageIcon sx={{ color: '#1565c0' }} />;
  }
  return <InsertDriveFileIcon sx={{ color: '#757575' }} />;
};

const getStatusChip = (status) => {
  const statusConfig = {
    pending: { label: 'Pending', color: 'default' },
    uploading: { label: 'Uploading...', color: 'info' },
    uploaded: { label: 'Ready', color: 'success' },
    processing: { label: 'Processing...', color: 'warning' },
    complete: { label: 'Complete', color: 'success' },
    error: { label: 'Error', color: 'error' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  return <Chip label={config.label} color={config.color} size="small" />;
};

function FileCard({ file, onRemove, progress }) {
  const isUploading = file.status === 'uploading';

  return (
    <Card
      sx={{
        mb: 1,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* File Icon */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getFileIcon(file.type)}
          </Box>

          {/* File Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.originalName || file.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(file.size)}
            </Typography>
          </Box>

          {/* Status Chip */}
          <Box>{getStatusChip(file.status)}</Box>

          {/* Remove Button */}
          <IconButton
            onClick={() => onRemove(file.id)}
            size="small"
            color="error"
            disabled={isUploading}
            sx={{ ml: 1 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Upload Progress */}
        {isUploading && progress !== undefined && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default FileCard;
